import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { routes } from '@/app/routes'
import { shellStrings as s } from '@/components/navigation/strings'
import { useUser } from '@/features/auth/useSession'
import { notificationsApi } from '@/features/notifications/notifications.api'
import { bucketDisplay, notificationBucket, notificationHref, relativeTime } from '@/features/notifications/notification-utils'
import { unreadCountKey, useUnreadCount } from '@/features/notifications/useUnreadCount'
import { notificationKeys } from '@/features/notifications/useNotifications'
import { invalidateFor } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { DsBadge, DsButton, DsIconButton, DsIconTile, DsIcons, DsPopover, DsSkeletonListRow, DsText } from '@/ds'
import type { AppNotification } from '@/types/models'

const PREVIEW = 8

export function NotificationsPopover() {
  const user = useUser()
  const unread = useUnreadCount()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const preview = useQuery({ queryKey: notificationKeys.preview, queryFn: () => notificationsApi.list({ page: 1, size: PREVIEW }), enabled: open, staleTime: 15_000 })

  const openItem = (n: AppNotification) => {
    setOpen(false)
    if (!n.read) {
      qc.setQueryData<number>(unreadCountKey, (c) => Math.max(0, (c ?? 1) - 1))
      void notificationsApi.markRead(n.id).finally(() => void invalidateFor('notification'))
    }
    const href = user ? notificationHref(n.data, user.defaultProfile) : null
    navigate(href ?? routes.notifications)
  }
  const markAll = async () => {
    qc.setQueryData<number>(unreadCountKey, 0)
    await notificationsApi.markAllRead().catch(() => undefined)
    await invalidateFor('notification')
    await preview.refetch()
  }

  return (
    <DsPopover
      open={open}
      onOpenChange={setOpen}
      width="lg"
      semanticLabel={s.notifications}
      trigger={
        <DsIconButton
          icon={DsIcons.bell}
          semanticLabel={unread > 0 ? `${s.notifications}, ${unread}` : s.notifications}
          badge={unread > 0 ? <DsBadge.Count value={unread} size="xs" /> : undefined}
        />
      }
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <DsText variant="title3">{s.notifications}</DsText>
        {unread > 0 && <DsButton variant="ghost" size="sm" label={s.markAllRead} onClick={() => void markAll()} />}
      </div>
      <div className="py-1">
        {preview.isPending ? (
          Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="px-4 py-1">
              <DsSkeletonListRow trailing={false} />
            </div>
          ))
        ) : preview.isError ? (
          <DsText variant="small" muted className="px-4 py-6 text-center">{s.notificationsError}</DsText>
        ) : preview.data.notifications.length === 0 ? (
          <DsText variant="small" muted className="px-4 py-6 text-center">{s.noNotifications}</DsText>
        ) : (
          <ul>
            {preview.data.notifications.map((n) => {
              const d = bucketDisplay[notificationBucket(n.type)]
              return (
                <li key={n.id}>
                  <button type="button" onClick={() => openItem(n)} className="flex w-full items-start gap-3 px-4 py-2.5 text-left outline-none hover:bg-muted focus-visible:bg-muted">
                    <DsIconTile icon={d.icon} tone={d.tone} size={32} />
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate text-small', n.read ? 'text-muted-foreground' : 'font-semibold text-foreground')}>{n.title}</span>
                      <span className="block truncate text-caption text-muted-foreground">{n.body}</span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-caption text-muted-foreground">{relativeTime(n.createdAt)}</span>
                      {!n.read && <span aria-hidden className="size-2 rounded-full bg-brand" />}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      <div className="border-t px-4 py-2 text-center">
        <Link to={routes.notifications} onClick={() => setOpen(false)} className="text-small-strong text-link outline-none hover:underline focus-visible:ring-focus">
          {s.viewAll}
        </Link>
      </div>
    </DsPopover>
  )
}
